/**
 * Split phone helpers for BaaS person contact (CountryDialCode / AreaCode / Number).
 */

export const DEFAULT_COUNTRY_DIAL_CODE = "55";

/** Fallback dial options when platform countries lack phone_code. */
export const FALLBACK_PHONE_DIAL_OPTIONS = [
  { code: "BR", dial: "55", name: "Brasil" },
  { code: "US", dial: "1", name: "United States" },
  { code: "PT", dial: "351", name: "Portugal" },
  { code: "ES", dial: "34", name: "España" },
  { code: "AR", dial: "54", name: "Argentina" },
  { code: "MX", dial: "52", name: "México" },
  { code: "GB", dial: "44", name: "United Kingdom" },
  { code: "DE", dial: "49", name: "Deutschland" },
  { code: "FR", dial: "33", name: "France" },
  { code: "AE", dial: "971", name: "United Arab Emirates" },
  { code: "CA", dial: "1", name: "Canada" },
];

export const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

export const createEmptyPhoneParts = () => ({
  countryDialCode: DEFAULT_COUNTRY_DIAL_CODE,
  areaCode: "",
  number: "",
});

/**
 * Builds unique dial-code options from platform countries (+ fallbacks).
 * @param {{ code?: string, name?: string, phoneCode?: string, phone_code?: string }[]} countries
 */
export const buildPhoneDialOptions = (countries = []) => {
  const byDial = new Map();

  const upsert = (code, dial, name) => {
    const dialDigits = digitsOnly(dial);
    if (!dialDigits) return;
    if (byDial.has(dialDigits)) return;
    byDial.set(dialDigits, {
      dial: dialDigits,
      code: String(code || "").toUpperCase(),
      name: name || code || dialDigits,
    });
  };

  for (const entry of countries) {
    upsert(
      entry?.code,
      entry?.phoneCode ?? entry?.phone_code,
      entry?.name,
    );
  }

  for (const entry of FALLBACK_PHONE_DIAL_OPTIONS) {
    upsert(entry.code, entry.dial, entry.name);
  }

  return [...byDial.values()].sort((a, b) => {
    const aDial = Number(a.dial);
    const bDial = Number(b.dial);
    if (aDial !== bDial) return aDial - bDial;
    return String(a.name).localeCompare(String(b.name));
  });
};

/** ISO-3166 alpha-2 → regional-indicator flag emoji (works in native `<option>`). */
export const countryCodeToFlagEmoji = (code) => {
  const iso = String(code || "")
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{2}$/.test(iso)) return "";
  return String.fromCodePoint(...[...iso].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

export const formatPhoneDialOptionLabel = ({ name, dial, code }) => {
  const flag = countryCodeToFlagEmoji(code);
  const label = `${name || code || ""} (+${dial})`;
  return flag ? `${flag} ${label}` : label;
};

/** Display mask for the local number field (area code is separate). BR: 9999-9999 / 99999-9999. */
export const formatLocalPhoneNumberDisplay = (value, countryDialCode = "") => {
  const digits = digitsOnly(value);
  if (digitsOnly(countryDialCode) === "55") {
    const clipped = digits.slice(0, 9);
    if (clipped.length <= 4) return clipped;
    if (clipped.length <= 8) return `${clipped.slice(0, 4)}-${clipped.slice(4)}`;
    return `${clipped.slice(0, 5)}-${clipped.slice(5)}`;
  }
  return digits.slice(0, 12);
};

/**
 * Prefer AccountPersonPhone parts from GET; fall back to parsing E.164.
 * Reads snake_case and camelCase in one pass so API `phone.number` is never dropped.
 */
export const mapPhonePartsFromPayload = (details = {}) => {
  const phone = details.phone ?? details.Phone ?? null;

  if (phone && typeof phone === "object") {
    const fromParts = {
      countryDialCode: digitsOnly(
        phone.country_dial_code ?? phone.countryDialCode ?? phone.CountryDialCode,
      ),
      areaCode: digitsOnly(phone.area_code ?? phone.areaCode ?? phone.AreaCode),
      number: digitsOnly(phone.number ?? phone.Number),
    };
    if (fromParts.countryDialCode && fromParts.areaCode && fromParts.number) {
      return fromParts;
    }
  }

  // Legacy string on phone field
  if (typeof phone === "string" && phone.trim()) {
    return splitE164ToParts(phone) || createEmptyPhoneParts();
  }

  const fromFlat = {
    countryDialCode: digitsOnly(details.country_dial_code ?? details.countryDialCode),
    areaCode: digitsOnly(details.area_code ?? details.areaCode),
    number: digitsOnly(
      details.phone_local_number ?? details.phoneLocalNumber ?? details.number,
    ),
  };
  if (fromFlat.countryDialCode && fromFlat.areaCode && fromFlat.number) {
    return fromFlat;
  }

  return (
    splitE164ToParts(details.phone_number ?? details.phoneNumber ?? details.PhoneNumber ?? "") ||
    createEmptyPhoneParts()
  );
};

export const splitE164ToParts = (phone) => {
  const digits = digitsOnly(phone);
  if (!digits || digits.length < 8) {
    return null;
  }

  if (digits.startsWith("55") && digits.length >= 12 && digits.length <= 13) {
    return {
      countryDialCode: "55",
      areaCode: digits.slice(2, 4),
      number: digits.slice(4),
    };
  }

  if (digits.startsWith("1") && digits.length === 11) {
    return {
      countryDialCode: "1",
      areaCode: digits.slice(1, 4),
      number: digits.slice(4),
    };
  }

  // Generic: 2-digit country + 2-digit area when long enough
  if (digits.length >= 10) {
    return {
      countryDialCode: digits.slice(0, 2),
      areaCode: digits.slice(2, 4),
      number: digits.slice(4),
    };
  }

  return null;
};

export const composeE164FromParts = ({ countryDialCode, areaCode, number } = {}) => {
  const country = digitsOnly(countryDialCode);
  const area = digitsOnly(areaCode);
  const local = digitsOnly(number);
  if (!country || !area || !local) {
    return "";
  }
  return `+${country}${area}${local}`;
};

export const formatPhonePartsForDisplay = (parts) => {
  const e164 = composeE164FromParts(parts);
  if (e164) return e164;
  const country = digitsOnly(parts?.countryDialCode);
  const area = digitsOnly(parts?.areaCode);
  const local = digitsOnly(parts?.number);
  if (!country && !area && !local) return "—";
  return `+${country || "?"} ${area || "?"} ${local || "?"}`.trim();
};

/**
 * True when split phone parts are filled, or a legacy single phone string / composed E.164 is usable.
 * Length rules stay intentionally permissive so UI step validation matches what users can enter.
 */
export const isPhonePartsComplete = (parts) => {
  // Legacy single-string phone (pre–PhonePartsFields drafts / older forms)
  if (typeof parts === "string") {
    const digits = digitsOnly(parts);
    if (digits.length >= 8 && digits.length <= 15) {
      return true;
    }
    const split = splitE164ToParts(parts);
    return Boolean(split && isPhonePartsComplete(split));
  }

  if (!parts || typeof parts !== "object") {
    return false;
  }

  const country = digitsOnly(parts.countryDialCode ?? parts.country_dial_code);
  const area = digitsOnly(parts.areaCode ?? parts.area_code);
  const local = digitsOnly(parts.number);

  if (country && area && local) {
    if (country === "55") {
      // BR: DDD 2 digits; local landline 8 or mobile 9 (allow slight over-entry)
      return area.length >= 2 && area.length <= 3 && local.length >= 8 && local.length <= 11;
    }
    if (country === "1") {
      return area.length === 3 && local.length >= 7 && local.length <= 10;
    }
    return local.length >= 4 && local.length <= 12;
  }

  const composed = composeE164FromParts({
    countryDialCode: country,
    areaCode: area,
    number: local,
  });
  return Boolean(composed && /^\+[1-9]\d{7,14}$/.test(composed));
};

export const toPhonePartsPayload = (parts) => ({
  country_dial_code: digitsOnly(parts?.countryDialCode),
  area_code: digitsOnly(parts?.areaCode),
  number: digitsOnly(parts?.number),
});
